import { useEffect, useState, useCallback } from 'react';

import { supabase } from '../../shared/services/supabaseService';

export interface FriendsGroup {
  id: string;
  name: string;
  group_code?: string;
  owner_id?: string;
}

export interface FriendsGroupMember {
  user_id: string;
  role: string;
}

/**
 * useFriendsData - fetches the current user's friends groups, members, and invitations from Supabase
 */
export function useFriendsData(userId: string | null) {
  const [groups, setGroups] = useState<FriendsGroup[]>([]);
  const [membersByGroup, setMembersByGroup] = useState<Record<string, FriendsGroupMember[]>>({});
  const [invitations, setInvitations] = useState<{ id: string; group_name?: string; sender_name?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFriendsData = useCallback(async () => {
    if (!userId) {
      setGroups([]);
      setMembersByGroup({});
      setInvitations([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch all friends groups memberships for the user
      const { data: memberships, error: membershipsError } = await supabase
        .from('friends_members')
        .select('*, friends(*), user_profiles(*)')
        .eq('user_id', userId);
      if (membershipsError) throw membershipsError;
      const userGroups = (memberships || []).map((membership) => membership.friends as FriendsGroup | null).filter(Boolean) as FriendsGroup[];
      // For each group, include its group_id (short code) directly from the social_circles table
      const groupsWithCode = userGroups.map((grp) => ({
        ...grp,
        group_code: grp.group_code // short code
      }));
      setGroups(groupsWithCode);

      // 2. For each group, fetch its members
      const membersByGrp: Record<string, FriendsGroupMember[]> = {};
      for (const grp of userGroups) {
        const { data: grpMembers, error: grpMembersError } = await supabase
          .from('friends_members')
          .select('*, user_profiles:user_profiles!inner(*)')
          .eq('group_id', grp.id);
        if (grpMembersError) throw grpMembersError;
        membersByGrp[grp.id] = (grpMembers || []) as FriendsGroupMember[];
      }
      setMembersByGroup(membersByGrp);

      // 3. Fetch invitations
      const { data: invitationData, error: invitationError } = await supabase
        .rpc('get_pending_friends_invitations_for_user', {
          uid: userId,
        });
      if (invitationError) {
        setInvitations([]);
        console.error('Error loading invitations', invitationError);
      } else {
        setInvitations(invitationData || []);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load friends data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchFriendsData();
    // Optionally, add realtime subscription here
  }, [fetchFriendsData]);

  return { groups, membersByGroup, invitations, loading, error, refetch: fetchFriendsData };
}
